import * as $ from "jquery";
import "signalr";
import { EventEmitter } from "events";
import RealtimeService from "./RealtimeService";
import StoreContainer from "../stores/StoreContainer";
import { RealtimeState } from "../types";

class RealtimeServiceSignalR implements RealtimeService {
  private _emitter = new EventEmitter();
  private _connection: SignalR.Hub.Connection;
  private _proxy: SignalR.Hub.Proxy;
  private _expectedState: RealtimeState;
  private _testingDisconnect = false;

  constructor() {
    this._expectedState = RealtimeState.Disconnected;
    this._connection = $.hubConnection(
      `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/signalr`
    );
    this._proxy = this._connection.createHubProxy("EngagementHub");
    this._proxy.connection.logging = true;

    this._proxy.connection.qs = {
      visitorId: StoreContainer.VisitorStore.getVisitorId(),
      engagementId: StoreContainer.EngagementStore.hasEngagementId()
        ? StoreContainer.EngagementStore.getEngagementId()
        : null,
      siteId: StoreContainer.SettingStore.getSiteId(),
      callId: StoreContainer.CallStore.getCallId()
    };

    // todo: need to write disconnect/reconnect logic. Be very careful, connection.stop()
    // is called explicitly when the visitor closes chat. Do not attempt to reconnect them
    // in that situation.

    this._connection.stateChanged((result) => this.handleStateChange(result));
  }

  handleStateChange(result: any) {
    switch (result.newState) {
      case 1:
        this.handleConnectedEvent();
        return;
      case 4:
        this.handleDisconnectEvent();
        return;
    }
  }

  handleConnectedEvent() {
    if (this._expectedState === RealtimeState.Connected) {
      //If we're already supposed to be connected, then this is a reconnect due to
      //an unexpected disconnect. Logic to refresh the engagement will most
      //likely need to occur, so fire off an event
      this._emitter.emit("reconnected");
    } else {
      this._emitter.emit("connected");
      this._expectedState = RealtimeState.Connected;
    }
  }

  handleDisconnectEvent() {
    if (this._expectedState === RealtimeState.Connected) {
      // Attempt to reconnect after 5 seconds. If we don't have
      // a delay here, we get into a reconnect/disconnect event
      // loop and spam the server with reconnect attempts
      setTimeout(() => {
        this.startReconnect();
      }, 5000);
    }
  }

  startReconnect() {
    if (this._testingDisconnect) {
      console.log("reconnect blocked because _testingDisconnect is true");
      return;
    }

    //for now, a reconnect can just be starting the connection again.
    //May need to improve this later to reconnect on an interval or something
    this.startConnection();
  }

  onConnect(callback: any) {
    this._emitter.addListener("connected", callback);
  }

  onReconnect(callback: any) {
    this._emitter.addListener("reconnected", callback);
  }

  visitorLeaveCall() {
    this._proxy.invoke("VisitorLeaveCall");
  }

  onReceiveEngagementContent(callback: any) {
    this._proxy.on("receiveEngagementContent", callback);
  }

  onReceiveSecureForm(callback: any) {
    this._proxy.on("receiveSecureForm", callback);
  }

  onReceiveLine(callback: any) {
    this._proxy.on("receiveLine", callback);
  }

  onUpdateQueuePosition(callback: any) {
    this._proxy.on("updateQueuePosition", callback);
  }

  onReceiveAgentTyping(callback: any) {
    this._proxy.on("receiveAgentTyping", callback);
  }

  onReceiveEndCall(callback: any) {
    this._proxy.on("endCall", callback);
  }

  onReceiveCall(callback: any) {
    this._proxy.on("receiveCall", callback);
  }

  onReceiveLanguageUpdate(callback: any) {
    this._proxy.on("receiveLanguageUpdate", callback);
  }

  onReceiveCobrowseRequest(callback: any) {
    this._proxy.on("receiveCobrowseRequest", callback);
  }

  onReceiveStopCobrowsing(callback: any) {
    this._proxy.on("receiveStopCobrowsing", callback);
  }

  async startConnection() {
    try {
      await this._proxy.connection.start();
    } catch (err) {
      console.error("Velaro chat connection failure");
      console.error(err);
    }
  }

  async stopConnection() {
    this._expectedState = RealtimeState.Disconnected;
    this._proxy.connection.stop();
  }

  getExpectedState() {
    return this._expectedState;
  }

  testDisconnect() {
    this._testingDisconnect = true;
    this.stopConnection();
  }

  testReconnect() {
    this._testingDisconnect = false;
    this.startConnection();
  }
}

export default RealtimeServiceSignalR;
